import React, { useContext, useRef, useState } from "react";
import EditButton from "../../components/EditButton/EditButton";
import { IconEdit } from "../../icons/edit";
import { IconMenu } from "../../icons/menu";
import { ISiteV2 } from "../../interfaces/Site";
import styles from "./Menu.module.scss";
import MenuItem from "./MenuItem/MenuItem";
import useOnClickOutside from "../../utils/useOnClickOutside";
import { useTranslation } from "../../../contexts/LanguageContext";
import { useElementSize } from "../../utils/useElementSize";
import { PreviewContext } from "../../../contexts/PreviewContext";

interface IProps {
    site: ISiteV2;
    editMode: false;
    editAvailable: boolean;
}

interface IPropsEditMode {
    site: ISiteV2;
    editMode: true;
    showEditMenu: () => void;
    editAvailable: boolean;
}

export const Menu = (props: IProps | IPropsEditMode) => {
    const isPreview = useContext(PreviewContext);
    const [toggled, setToggled] = useState(false);
    const ref = useOnClickOutside(() => setToggled(false), toggled);
    const { getInTranslation } = useTranslation();

    const toggleMenu = () => {
        setToggled(!toggled);
    };

    const allMenuItemsIsHidden = (): boolean => {
        if (props.site.siteModules && props.site.siteModules.length) {
            const numberOfNotHiddenMenuItems = props.site.siteModules.filter((module) => {
                return module.hideInMenu !== true;
            }).length;
            return numberOfNotHiddenMenuItems ? false : true;
        } else {
            return true;
        }
    };

    const elementRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const elementSize = useElementSize(elementRef, isPreview ? 300 : 0);
    const wrapperSize = useElementSize(wrapperRef, isPreview ? 300 : 0);

    const calculateNavigationFits = () => {
        if (!elementSize || !wrapperSize) {
            return null;
        }
        const errorMargin = 2;
        const menuWidth = elementSize.width;
        const menuWrapperWidth = wrapperSize.width - errorMargin;

        if (menuWidth >= menuWrapperWidth) {
            return false;
        } else {
            return true;
        }
    };
    const navigationFits = calculateNavigationFits();

    if (props.editMode !== true && allMenuItemsIsHidden()) {
        return null;
    }

    // If this should be added as editable thing some day it should be in edit menu modal. But right now we settle for swedish and english.
    const menuTitle = getInTranslation({ sv: "Meny", en: "Menu" });

    return (
        <>
            <div
                className={`${styles[props.site.theme]} ${toggled ? styles.toggled : ""} ${
                    props.editAvailable ? styles.editAvailable : ""
                } ${navigationFits ? styles.navigationFits : ""}`}
                ref={ref}
                data-test-id="public-menu"
            >
                <div
                    className={`${styles.toggleButton} ${toggled ? styles.toggled : ""} ${
                        navigationFits === null ? styles.hideUntilMesurmentIsDone : null
                    }`}
                    onClick={toggleMenu}
                >
                    <IconMenu></IconMenu>
                    {menuTitle}
                </div>
                <nav
                    className={`${styles.menu} ${toggled ? styles.toggled : ""} ${
                        navigationFits ? styles.navigationFits : ""
                    } ${navigationFits === null ? styles.hideUntilMesurmentIsDone : null}`}
                >
                    <div className={styles.innerWrapper}>
                        <div className={styles.editMenuButtonWrapper}>
                            {props.editMode === true ? (
                                <EditButton callback={props.showEditMenu}>
                                    <>
                                        <IconEdit></IconEdit>
                                        Ändra meny
                                    </>
                                </EditButton>
                            ) : null}
                        </div>

                        <ul>
                            {props.site.siteModules
                                .filter(
                                    (module) => module.hideInMenu !== true && (module.hide !== true || props.editMode)
                                )
                                .map((module) => (
                                    <MenuItem
                                        key={module.id}
                                        navigationFits={!!navigationFits}
                                        siteModule={module}
                                        site={props.site}
                                        closeMenu={() => setToggled(false)}
                                    />
                                ))}
                        </ul>
                        <div className={styles.rightSpacer}></div>
                    </div>
                </nav>
                <div className={`${styles.menu} ${styles.navigationFits} ${styles.measurementOnly}`} ref={wrapperRef}>
                    <div className={styles.innerWrapper} ref={elementRef}>
                        <div className={styles.editMenuButtonWrapper}></div>

                        <ul>
                            {props.site.siteModules
                                .filter(
                                    (module) => module.hideInMenu !== true && (module.hide !== true || props.editMode)
                                )
                                .map((module) => (
                                    <MenuItem
                                        key={module.id}
                                        navigationFits={true}
                                        measurementOnly={true}
                                        siteModule={module}
                                        site={props.site}
                                        closeMenu={() => setToggled(false)}
                                    />
                                ))}
                        </ul>
                        <div className={styles.rightSpacer}></div>
                    </div>
                </div>
            </div>
        </>
    );
};
